import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';

import AttachmentComponent from '../../../../components/Attachments/AttachmentComponent';

import { RootState } from '../../../../store/store';
import { setGlobalSave } from '../../../../store/reducers/globalSaveReducer';
import {
	useAddOrUpdateAttachmentsMutation,
	useGetAttachmentsQuery,
} from '../../../../store/apiSlices/attachmentsApiSlice';
import { useGetRecruitDetailsByIdQuery } from '../../../../store/apiSlices/recruitApiSlice';

import attachment from '../../../../assets/icons/attachment.svg';

import { toastHandler } from '../../../../utils/toastHandler';
import { FormikRefs, PromiseTypes } from '../../../../constants';
import { StatusCode } from '../../../../enums/httpStatus.enum';

import { useFormikRefs } from '../../../../hooks/useFormikRef';
import { MAX_FILES } from '../../../../constants';
import { setAttachmentProperties } from '../../../../store/reducers/attachmentsReducer';
import { setDirty } from '../../../../store/reducers/unSavedChangesReducer';
import useResetComponent from '../../../../hooks/useComponentReset';

const Attachments = () => {
	const dispatch = useDispatch();
	const [searchParams] = useSearchParams();
	const { registerRef, unregisterRef, updateRefs } = useFormikRefs();
	const recruit_id = searchParams.get('id');
	const globalSave = useSelector((state: RootState) => state.globalSave);
	const attachmentsReducer = useSelector(
		(state: RootState) => state.attachmentsReducer,
	);
	const formKey = FormikRefs.attachmentsFormikRef;
	const userInfo = useSelector((state: RootState) => state.user);
	const unSavedChanges = useSelector(
		(store: RootState) => store.unSavedChangesReducer,
	);
	const recruitDetails = useGetRecruitDetailsByIdQuery({
		userId: recruit_id as string,
		userEmail: userInfo.user_email,
	});
	const attachmentsData = useGetAttachmentsQuery({
		recruitId: recruit_id ?? '',
		userEmail: userInfo.user_email,
	});
	const [uploadFile, uploadFileStatus] = useAddOrUpdateAttachmentsMutation();

	useResetComponent({ reset: () => handleCancel() });

	useEffect(() => {
		if ((attachmentsData?.currentData?.value?.length ?? 1) < 0) {
			dispatch(
				setAttachmentProperties({
					isUploadable: true,
				}),
			);
		}
	}, [attachmentsData]);

	const formik: FormikProps<{ files: File[] }> = useFormik<{ files: File[] }>(
		{
			initialValues: {
				files: [],
			},
			validationSchema: Yup.object({
				files: Yup.array().test('length', (value) => {
					const length = value?.length || 0;
					if (length > 15) {
						toastHandler({
							isError: true,
							msg: 'Only 15 files are allowed at once',
						});
						return false;
					}
					return true;
				}),
			}),
			onSubmit: async (values) => {
				if (formik.values?.files?.length <= 0) {
					toastHandler({
						isError: true,
						msg: 'Please upload at least one file',
					});
					return;
				}
				dispatch(
					setGlobalSave({
						isGlobalSaveActivated: false,
						apisCalled: {
							...globalSave.apisCalled,
							isAttachmentAPICalled: true,
						},
					}),
				);
				dispatch(
					setAttachmentProperties({
						isUploadable: false,
					}),
				);
				const _uploadFile = async (file: File) => {
					try {
						const res = await uploadFile({
							recruitid: recruit_id!,
							userEmail: userInfo.user_email,
							file: file,
						});
						if (res.data?.statusCode == StatusCode.SERVER_ERROR) {
							throw res.data.value as string;
						}
						if (res.data?.statusCode == StatusCode.UNAUTHORIZED) {
							throw res.data.message as string;
						}
					} catch (e) {
						toastHandler({
							isError: true,
							msg: `Failed to upload attachments`,
						});
						throw e;
					}
				};
				try {
					toastHandler({
						isLoading: true,
						msg: 'Uploading attachments',
					});
					const recruitItems = await Promise.allSettled(
						values.files.map(async (file) => {
							return _uploadFile(file);
						}),
					);
					const areAllFilesUploaded = recruitItems?.every(
						(e) => e.status === PromiseTypes.fulFilled,
					);
					if (areAllFilesUploaded) {
						toastHandler({
							isError: false,
							msg: `Successfully uploaded attachments`,
						});
					}
					dispatch(
						setGlobalSave({
							isGlobalSaveActivated: false,
							apisCalled: {
								...globalSave.apisCalled,
								isAttachmentAPICalled: false,
							},
						}),
					);
					recruitDetails?.refetch?.();
					formik.resetForm();
				} catch (e: unknown) {
					toastHandler({
						isError: true,
						msg: e as string,
					});
				}
				dispatch(
					setAttachmentProperties({
						isUploadable: false,
					}),
				);
			},
		},
	);

	useEffect(() => {
		registerRef(formKey, { current: formik });

		return () => {
			unregisterRef(formKey);
		};
	}, [formKey, formik, registerRef, unregisterRef]);

	useEffect(() => {
		updateRefs?.();
		dispatch(
			setDirty({
				isDirty: formik.dirty || false,
				isModalOpen: false,
				isNavigationAllowed: true,
			}),
		);
	}, [formik.dirty]);

	const handleUpload = () => {
		if (!userInfo.roles.adminAppAttachRecruitDocs) return;
		formik.resetForm();
	};

	const handleDone = async () => {
		if (!userInfo.roles.adminAppAttachRecruitDocs) return;
		await formik.submitForm();
	};

	const handleCancel = () => {
		if (formik.dirty) {
			dispatch(
				setDirty({
					isDirty: formik.dirty,
					isModalOpen: true,
					isNavigationAllowed: false,
					handleConfirm: handleModalConfirm,
					handleCancel: handleModalCancel,
				}),
			);
		} else {
			handleModalConfirm();
		}
	};

	const handleModalCancel = () => {
		dispatch(
			setDirty({
				...unSavedChanges,
				isModalOpen: false,
			}),
		);
	};

	const handleModalConfirm = () => {
		dispatch(
			setAttachmentProperties({
				isUploadable: false,
			}),
		);
		formik.resetForm();
	};

	const handleDeleteAttachment = () => {
		recruitDetails?.refetch?.();
	};

	return (
		<>
			{attachmentsData.isSuccess && (
				<div className="flex flex-col bg-white p-4 gap-y-4 rounded-lg ">
					<div className="flex items-center justify-between">
						<div className="text-xl font-bold">Attachments</div>
						{attachmentsReducer.isUploadable ? (
							<div className="flex gap-x-2">
								<p
									className="cursor-pointer"
									onClick={() => handleCancel()}
								>
									Cancel
								</p>
								<p
									className="cursor-pointer"
									onClick={() => {
										handleDone();
									}}
								>
									Done
								</p>
							</div>
						) : (
							<div
								className={`flex ${globalSave.apisCalled.isAttachmentAPICalled || globalSave.isGlobalSaveActivated ? '' : 'cursor-pointer'}`}
								onClick={() => {
									if (
										!(
											globalSave.apisCalled
												.isAttachmentAPICalled ||
											globalSave.isGlobalSaveActivated
										)
									) {
										dispatch(
											setAttachmentProperties({
												isUploadable: true,
											}),
										);
										handleUpload();
									}
								}}
							>
								<img src={attachment} alt="" /> <p>Upload</p>
							</div>
						)}
					</div>
					<div className="max-h-[50vh] overflow-auto pr-5">
						<AttachmentComponent
							formik={formik}
							isUploadable={attachmentsReducer.isUploadable}
							handleUpload={handleUpload}
							attachmentsData={
								attachmentsData?.currentData?.value ?? []
							}
							handleDelete={handleDeleteAttachment}
							maxFiles={MAX_FILES}
						/>
					</div>
				</div>
			)}
		</>
	);
};
export default Attachments;
